import { GetStaticPaths, GetStaticProps } from "next";

import { fetchPageBySlug } from "@clients/contentfulClient";
import Meta from "@components/Meta/meta";
import EventPage from "@containers/EventPage";
import InsuranceClaimPage from "@containers/InsuranceClaimPage";
import InsurancePage from "@containers/InsurancePage";
import LandingPage from "@containers/Landingpage";
import Layout from "@containers/Layout/layout";
import NewsPage from "@containers/NewsPage";
import ReportPage from "@containers/ReportPage";
import StandardPage from "@containers/StandardPage";
import { Event, trackEvent } from "@utils/events";
import { socialMediaDefaultImage } from "@utils/imageHelpers/imageHelper";
import { getStaticPropsInfo } from "@utils/pageHelpers/getStaticPropsInfo";
import { pathHasContent } from "@utils/pageHelpers/pathHasContent";
import { getBaseUrl, replaceImageVaultBasePath } from "@utils/urlResolver";

import { PageCollectionType_pageCollection_items_content } from "globalQueries/contentfulTypes/PageCollectionType";
import { GetSiteTreeStructure } from "services/SiteTreeStructureFactory";
import { UrlStructureService, getUrlStructureItems } from "services/UrlStructureService";
import { getLayoutData } from "services/layoutService";

const IngenMissadErsattning = ({ page, currentUrl, layoutData }): JSX.Element => {
  const renderPage = (
    content: PageCollectionType_pageCollection_items_content,
    data: any,
    leftMenuItems: any
  ) => {
    const pageType = content?.__typename?.toLowerCase();
    switch (pageType) {
      case "landingpage":
        return <LandingPage pageData={data} />;
      case "standardpage":
        return <StandardPage pageData={data} leftMenuItems={leftMenuItems} />;
      case "insurancepage":
        return <InsurancePage pageData={data} />;
      case "insuranceclaimpage":
        return <InsuranceClaimPage pageData={data} />;
      case "eventpage":
        return <EventPage pageData={data} />;
      case "newspage":
        return <NewsPage pageData={data} />;
      case "reportpage":
        return <ReportPage pageData={data} />;
      default:
        return null;
    }
  };

  const { seo } = page?.slug;
  const base = getBaseUrl();
  const metaImage =
    base +
    (replaceImageVaultBasePath(seo?.ivSocialMediaImage?.[0]?.socialMediaImageVersion, false) ||
      socialMediaDefaultImage?.url);
  return (
    <Layout {...layoutData}>
      <Meta
        title={seo?.title}
        description={seo?.description}
        keywords={seo?.keywords?.map((i: string) => i)}
        image={metaImage}
        imageAlt={
          seo?.ivSocialMediaImage?.[0]?.Metadata?.[0]?.Value ||
          socialMediaDefaultImage?.imageAltText
        }
        canonicalUrl={seo?.canonicalUrl}
        url={base + currentUrl}
        noIndex={seo?.noIndex}
        noFollow={seo?.noFollow}
        breadcrumbs={layoutData?.siteHeaderData?.breadCrumbLinks}
      />
      {renderPage(page?.slug?.content, page?.slug?.data, layoutData?.siteHeaderData?.menuItems)}
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = (await getUrlStructureItems(false))
    .filter((item) => item.url.startsWith("/ingen-missad-ersattning"))
    .map((item) => item.url);
  return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const info = getStaticPropsInfo(context, "/ingen-missad-ersattning", "ingen-missad-ersattning");

  const [urlStructure, siteTreeStructure] = await Promise.all([
    UrlStructureService(info.preview),
    GetSiteTreeStructure(info.preview),
  ]);

  const layoutData = await getLayoutData(
    urlStructure,
    siteTreeStructure,
    info.path,
    info.preview,
    false
  );

  trackEvent(Event.GET_STATIC_PROPS, { path: info.path, preview: info.preview });

  const page = pathHasContent(urlStructure, info.path, info.preview)
    ? await fetchPageBySlug(urlStructure, info.slug, info.pageNumber, info.preview)
    : 404;

  if (page === 404) {
    return {
      notFound: true,
      revalidate: 120,
    };
  }

  return {
    props: {
      layoutData,
      page,
      currentUrl: info.path,
      preview: info.preview,
    },
    revalidate: 60,
  };
};

export default IngenMissadErsattning;
